import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { catchError, forkJoin, Subject, takeUntil, of } from 'rxjs';
import { BroadcastService } from 'app/broadcast.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Store } from '@ngrx/store';
import { selectConnectedUser } from 'app/ngrx/selectors/user.selectors';
import { MessageNotificationService } from 'app/shared/services/message-notification.service';
import { getNotificationDisplayMeta, isPartnerAdmin, encrypt } from 'app/shared/utils';
import { CompanyService } from 'app/shared/services/companyservice.service';
import { PartnersService } from 'app/shared/services/partners.service';
import { NOTIFICATION_REDIRECT_PAGES } from 'app/shared/constants/notification-redirect-pages.const';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications'
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _currentUserId: string | null = null;
    currentPage: number = 1;
    pageSize: number = 4;
    totalCount: number = 0;
    isLoading: boolean = false;
    private _currentCompanyId: string | null = null;
    isAdmin: boolean = false;
    currentPartner: any;
    currentMandataire: any;
    private _activeCompanyIds: string[] = [];
    isMultipleCompanies: boolean = false;
    activeCategory: string = 'Tout';

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _broadcastService: BroadcastService,
        private _store: Store,
        private _messageNotificationService: MessageNotificationService,
        private _companyService: CompanyService,
        private _partnersService: PartnersService
    ) {
    }

    ngOnInit(): void {
        this._store.select(selectConnectedUser).pipe(takeUntil(this._unsubscribeAll)).subscribe((user: any) => {
            if (!user) return;

            this._currentUserId = user.id || user.Id || user.guidId || null;
            this.isAdmin = isPartnerAdmin(user.user_UserProfiles || user.User_UserProfiles);
            const userCompanyId = user.companyId || user.CompanyId || null;
            this.setupUserContext(userCompanyId);
        });

        // Broadcast listener
        this._broadcastService.sicaNotification$.pipe(takeUntil(this._unsubscribeAll)).subscribe((result: any) => {
            const companyId = result.companyId || this._currentCompanyId;
            if (companyId && !this.isMultipleCompanies) {
                this._currentCompanyId = companyId;
                this.currentPage = 1;
                this.fetchNotifications();
            }
        });
    }

    private setupUserContext(defaultCompanyId: string | null): void {
        this.currentPartner = localStorage.getItem('currentPartner_localstorage') ? JSON.parse(localStorage.getItem('currentPartner_localstorage')) : null;
        const currentMandataire = localStorage.getItem('currentMandataire_localstorage') ? JSON.parse(localStorage.getItem('currentMandataire_localstorage')) : null;
        const tousLesPartenaires = localStorage.getItem('tousPartenaires_localstorage') ? JSON.parse(localStorage.getItem('tousPartenaires_localstorage')) : false;

        if (this.isAdmin && this.currentPartner && !tousLesPartenaires) {
            this.loadSinglePartnerContext(this.currentPartner);
        }
        else if (this.isAdmin && currentMandataire) {
            this.loadMandataireContext(currentMandataire);
        }
        else {
            this.loadStandardUserContext(defaultCompanyId);
        }
    }

    private loadMandataireContext(mandataire: any): void {
        this.isMultipleCompanies = true;
        this._partnersService.getPartnersByMandate(mandataire.id || mandataire.numericId).subscribe({
            next: (partners: any[]) => {
                if (!partners || partners.length === 0) return;

                const lookupRequests = partners.map(p => ({
                    siret: p.siret,
                    codeClient: p.code
                }));

                this._companyService.getCompaniesBySirets(lookupRequests).subscribe({
                    next: (companies: any[]) => {
                        this._activeCompanyIds = companies
                            .filter(c => c && (c.id || c.Id))
                            .map(c => c.id || c.Id);
                            
                        if (this._activeCompanyIds.length > 0) {
                            this.fetchNotifications();
                        }
                    },
                    error: (err) => console.error('Error fetching bulk companies:', err)
                });
            }
        });
    }

    private loadSinglePartnerContext(partner: any): void {
        this.isMultipleCompanies = false;
        if (!partner.siret && !partner.code) return;

        this._companyService.getCompanyBySiret(partner.siret, partner.code).subscribe({
            next: (company: any) => {
                this._currentCompanyId = company?.id || company?.Id || null;
                if (this._currentCompanyId) this.fetchNotifications();
            }
        });
    }

    private loadStandardUserContext(companyId: string | null): void {
        this.isMultipleCompanies = false;
        this._currentCompanyId = companyId;
        if (this._currentCompanyId) this.fetchNotifications();
    }

    private fetchNotifications(isAppend: boolean = false): void {
        if (this.isLoading) return;
        if (this.isMultipleCompanies && this._activeCompanyIds.length === 0) return;
        if (!this.isMultipleCompanies && !this._currentCompanyId) return;

        this.isLoading = true;

        const request$ = this.isMultipleCompanies
            ? this._messageNotificationService.getByCompanyIds(this._activeCompanyIds, this._currentUserId, this.activeCategory,this.currentPage, this.pageSize)
            : this._messageNotificationService.getByCompanyId(this._currentCompanyId, this._currentUserId, this.activeCategory,this.currentPage, this.pageSize);

        request$.subscribe({
            next: (response: any) => this.mapNotificationResponse(response, isAppend),
            error: (err) => {
                console.error('Error fetching notifications:', err);
                this.isLoading = false;
            }
        });
    }

    private mapNotificationResponse(response: any, isAppend: boolean): void {
        const items = response.data || response || [];
        this.totalCount = response.totalCount || items.length;
        this.unreadCount = response.unreadCount ?? this.totalCount;



        const mapped = items.map((notif: any) => {
            const displayMeta = getNotificationDisplayMeta(notif.iconCode || notif.IconCode, notif.iconColor || notif.IconColor);
            const rawTime = notif.notificationDate || null;
            const utcTime = rawTime ? (rawTime.endsWith('Z') ? rawTime : rawTime + 'Z') : null;
            const rawRedirectUrl = notif.redirectUrl || notif.RedirectUrl;
            const navigation = this.resolveNotificationLink(rawRedirectUrl);

            return {
                id: notif.id || notif.Id,
                title: notif.title || notif.Title,
                description: notif.content,
                time: utcTime || null,
                read: notif.isSeen || false,
                companyId: notif.companyId || notif.CompanyId || this._currentCompanyId,
                icon: displayMeta.icon,
                bgColorClass: displayMeta.bgColorClass,
                iconColorClass: displayMeta.iconColorClass,
                category: notif.category || 'Messages',
                link: navigation?.link,
                useRouter: navigation?.useRouter || false
            };
        });

        this.notifications = isAppend ? [...this.notifications, ...mapped] : mapped;
        this.isLoading = false;
        this._changeDetectorRef.markForCheck();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    toggleRead(notification: Notification): void {
        if (notification.read) {
            return;
        }

        notification.read = true;
        const targetCompanyId = notification.companyId || this._currentCompanyId;

        if (notification.read) {
            this.unreadCount = Math.max(0, this.unreadCount - 1);
        } else {
            this.unreadCount++;
        }
        this._changeDetectorRef.markForCheck();

        if (targetCompanyId && this._currentUserId) {

            this._messageNotificationService.toggleSingleRead(notification.id, targetCompanyId, this._currentUserId)
                .subscribe({
                    next: (res) => console.log('5. API Success:', res),
                    error: (err) => console.error('5. API Error:', err)
                });
        } else {
            console.warn('API Call Skipped: Missing Company ID or User ID!');
        }
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {
        // Delete the notification
        this._notificationsService.delete(notification.id).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(notification => !notification.read).length;
        }

        this.unreadCount = count;
    }

    onScroll(event: any): void {
        const element = event.target;
        if (element.scrollHeight - Math.ceil(element.scrollTop) <= element.clientHeight + 100) {
            this.loadMore();
        }
    }

    markAllAsRead(): void {
        if (!this._currentUserId) {
            console.warn('Cannot mark as read: User ID is missing.');
            return;
        }

        if (this.isMultipleCompanies && this._activeCompanyIds.length > 0) {
            this._messageNotificationService.markAllAsReadByCompanyIds(this._activeCompanyIds, this._currentUserId)
                .subscribe({
                    next: () => {
                        if (this.notifications && this.notifications.length > 0) {
                            this.notifications.forEach(notification => notification.read = true);
                        }
                        this.unreadCount = 0;
                        this._changeDetectorRef.markForCheck();
                    },
                    error: (err) => console.error('Error bulk marking notifications:', err)
                });
        }
        else if (this._currentCompanyId) {
            this._messageNotificationService.markAllAsRead(this._currentCompanyId, this._currentUserId).subscribe({
                next: () => {
                    if (this.notifications && this.notifications.length > 0) {
                        this.notifications.forEach(notification => notification.read = true);
                    }
                    this.unreadCount = 0;
                    this._changeDetectorRef.markForCheck();
                },
                error: (err) => console.error('Error marking all notifications as read:', err)
            });
        }
    }

    loadMore(): void {
        if (!this.isLoading && this.notifications && this.notifications.length < this.totalCount) {
            this.currentPage++;
            this.fetchNotifications(true);
        }
    }

    getRelativeTime(time: string): string {
        if (!time) return '';

        const date = new Date(time);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return "À l'instant";
        }

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `Il y a ${diffInMinutes} min`;
        }

        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfNotifDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const diffInCalendarDays = Math.round((startOfToday.getTime() - startOfNotifDay.getTime()) / (1000 * 60 * 60 * 24));

        if (diffInCalendarDays === 0) {
            const diffInHours = Math.floor(diffInMinutes / 60);
            return `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
        }

        if (diffInCalendarDays === 1) {
            return 'Hier';
        }

        if (diffInCalendarDays < 30) {
            return `Il y a ${diffInCalendarDays} jours`;
        }

        const diffInMonths = Math.floor(diffInCalendarDays / 30);
        if (diffInMonths < 12) {
            return `Il y a ${diffInMonths} mois`;
        }

        const diffInYears = Math.floor(diffInCalendarDays / 365);
        return `Il y a ${diffInYears} an${diffInYears > 1 ? 's' : ''}`;
    }

    /**
     * Absolute date shown in the tooltip on hover: French format "27 août, 19h12" (24h, no AM/PM).
     */
    getAbsoluteTime(time: string): string {
        if (!time) return '';

        const date = new Date(time);
        const day = date.getDate();
        const month = date.toLocaleDateString('fr-FR', { month: 'long' });
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${day} ${month}, ${hours}h${minutes}`;
    }

    private resolveNotificationLink(redirectUrl: string): { link: string; useRouter: boolean } | null {
        if (!redirectUrl) return null;

        const matchingPage = NOTIFICATION_REDIRECT_PAGES.find(page => {
            const staticPrefix = page.value.substring(0, page.value.indexOf('{'));
            return redirectUrl.startsWith(staticPrefix);
        });

        let link = redirectUrl;
        if (matchingPage?.encryptId) {
            const lastSlashIndex = redirectUrl.lastIndexOf('/');
            const staticPart = redirectUrl.substring(0, lastSlashIndex + 1);
            const idSegment = redirectUrl.substring(lastSlashIndex + 1);
            link = staticPart + encrypt(idSegment);
        }

        return { link, useRouter: true };
    }

    setCategory(category: string): void {
        if (this.activeCategory === category) return;

        this.activeCategory = category;
        this.currentPage = 1;
        this.notifications = []; 
        this.fetchNotifications();
    }
}
